import { map, finalize, catchError, tap, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '../../../app.config';
import { DataService } from '../../../core/service/base/data.service';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { Invoice, InvoiceCategory } from '../../model/invoice';
import { IAppConfig } from './../../../app.config';
import { NotificationBarService } from '../../../core/service/notification-bar.service';
import { Company } from '../../../rent/model/company';
import { InvoicePeriodItem } from '../../model/invoice-period';
import { Contract } from '../../../rent/model/contract';
import { LocalDate } from '../../../core/model/local-date';
import { DirectDebit } from '../../model/direct-debit';
import { Property } from '../../../rent/model/property';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { InvoiceAccounting, InvoiceAccountingBalance } from '../../model/invoice-accounting';
import { Money } from '../../../core/model/money';
import { CreditInvoiceModel, CreditInvoiceResultModel } from '../../model/credit-invoice';
import { InvoiceDeliveryMethod } from '../../model/const/enum';
import { DebtCollectionLog } from '../../model/debt-collection-log';
import { InvoiceRow } from '../../model/invoice-row';
import { InvoiceRecipientRecentLeaseAgreement } from '../../model/invoice-recipient-recent-lease-agreement';
import { UnpaidInvoiceSelection } from '../../model/unpaid-invoice-selection';

@Injectable()
export class InvoiceService extends DataService<Invoice> {
  private readonly _defaultFilterFields: string[] = [
    'category',
    'company',
    'periodFrom',
    'periodTo',
    'invDeliveryMethod',
    'emlDeliveryStatusKind',
    'invoiceNumber',
    'recipientName',
    'property',
    'directDebit',
    'vatInvoice',
    'invPeriodMonths',
    'invoiceType',
  ];

  constructor(
    private notificationBar: NotificationBarService,
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(client, config, messageService, Invoice, progressBar, 'invoice');
  }

  protected createModel(data: any): Invoice {
    const model = super.createModel(data);
    if (data.leaseAgreements instanceof Array) {
      model.leaseAgreements = data.leaseAgreements.map(item => Object.assign(new Contract(item.category), item));
    }
    if (data.directDebits instanceof Array) {
      model.directDebits = data.directDebits.map(item => Object.assign(new DirectDebit(), item));
    }

    if (data.debtCollectionLogs instanceof Array) {
      model.debtCollectionLogs = data.debtCollectionLogs.map(item => Object.assign(new DebtCollectionLog(), item));
    }

    if (data.amountAndTextRows instanceof Array) {
      model.amountAndTextRows = data.amountAndTextRows.map(item => new InvoiceRow(item));
    }

    return model;
  }

  getCreditInvoiceModel(invoiceId: string): Observable<CreditInvoiceModel> {
    this.progressBar.start();
    return this.client
      .get<CreditInvoiceModel>(`${this.endpoint}/credit-invoice-data/${invoiceId}`, { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  previewCredit(model: CreditInvoiceModel): Observable<CreditInvoiceResultModel> {
    this.progressBar.start();
    return this.client
      .put<CreditInvoiceResultModel>(`${this.endpoint}/preview-credit-invoice`, JSON.stringify(model), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  registerCustomerLoss(model: any): Observable<Money> {
    this.progressBar.start();
    return this.client
      .post<Money>(`${this.endpoint}/customer-loss`, JSON.stringify(model), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  hasOngoingAmortizationPlan(invoiceId: string): Observable<boolean> {
    this.progressBar.start();
    return this.client
      .get<boolean>(`${this.endpoint}/ongoing-amortization-plan/${invoiceId}`, { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  creditInvoice(model: CreditInvoiceModel): Observable<CreditInvoiceResultModel> {
    this.progressBar.start();
    return this.client
      .post<CreditInvoiceResultModel>(`${this.endpoint}/credit-invoice`, JSON.stringify(model), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  sendToDebtCollection(invoiceId: string) {
    this.progressBar.start();
    return this.client.post(`${this.endpoint}/debt-collection/${invoiceId}`, {}, { headers: { 'Content-Type': 'application/json' } }).pipe(
      tap(() => this.notification.showSuccess('DEBT_COLLECTION.CREATED')),
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  sendToDebtCollectionFilter(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<any> {
    if (includedIds.length > 0) {
      return this.sendToDebtCollectionMany(includedIds);
    }
    this.progressBar.start();
    return this.client
      .post<any>(
        `${this.endpoint}/debt-collection-filter`,
        { filter: this.prepareFilter(filter), includedIds: includedIds, excludedIds: excludedIds, affectedItemsCount: affectedItemsCount },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        tap(() => this.notification.showSuccess('DEBT_COLLECTION.CREATED')),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  sendToDebtCollectionMany(invoiceIds: string[]): Observable<any[]> {
    this.progressBar.start();
    return this.client
      .put<any[]>(`${this.endpoint}/debt-collection-many`, JSON.stringify(invoiceIds), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        tap(() => this.notification.showSuccess('DEBT_COLLECTION.CREATED')),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  attest(invoiceId: string): Observable<any> {
    this.progressBar.start();
    return this.client.put(`${this.endpoint}/attest`, JSON.stringify(invoiceId), { headers: { 'Content-Type': 'application/json' } }).pipe(
      tap(() => this.notification.showSuccess('SUCCESS.INVOICE_ATTESTED')),
      catchError(error => {
        this.notification.showError(error);
        throw new Error(`Not possible to attest invoice ${invoiceId}.`);
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  attestByFilter(selection: { filter: any; includedIds: string[]; excludedIds: string[]; affectedItemsCount: number }): Observable<any> {
    if (selection.includedIds.length > 0) {
      return this.attestMany(selection.includedIds);
    }
    this.progressBar.start();
    selection.filter = this.prepareFilter(selection.filter);
    return this.client.post<any>(`${this.endpoint}/attest-filter`, selection, { headers: { 'Content-Type': 'application/json' } }).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      }),
      tap(() => this.notification.showSuccess('SUCCESS.INVOICES_SENT')),
      finalize(() => this.progressBar.stop())
    );
  }

  attestMany(invoiceIds: string[]): Observable<any[]> {
    this.progressBar.start();
    return this.client
      .put<any[]>(`${this.endpoint}/attest-many`, JSON.stringify(invoiceIds), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        tap(() => this.notification.showSuccess('SUCCESS.INVOICES_ATTESTED')),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  createGeneralInvoice(value: any, rows: InvoiceRow[], appendixes: any[]): Observable<any> {
    this.progressBar.start();

    const formData = new FormData();
    formData.append('jsonData', JSON.stringify({ ...value, rows: rows }));

    for (let file of appendixes) {
      formData.append('appendixes[]', file, file.name);
    }

    return this.client.post(`${this.endpoint}/create-general`, formData).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  override change(model: Invoice, options: { notifyUser: boolean } = { notifyUser: true }): Observable<any> {
    if (!model.newAppendixes || model.newAppendixes.length === 0) {
      this.progressBar.start();
      return this.client.put(this.endpoint, JSON.stringify(model), { headers: { 'Content-Type': 'application/json' } }).pipe(
        tap(() => {
          if (options && options.notifyUser) {
            this.notification.showSuccess();
          }
        }),
        finalize(() => this.progressBar.stop())
      );
    }

    this.progressBar.start();

    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(model.toJSON()));

    for (let file of model.newAppendixes) {
      formData.append('appendixes[]', file, file.name);
    }

    return this.client.put(this.endpoint, formData).pipe(
      tap(() => {
        if (options && options.notifyUser) {
          this.notification.showSuccess();
        }
      }),
      catchError(error => {
        this.notification.showError(error);
        throw error;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  credit(form: any): Observable<Invoice> {
    this.progressBar.start();
    return this.client.put<Invoice>(`${this.endpoint}/credit`, JSON.stringify(form)).pipe(
      map(data => this.createModel(data)),
      catchError(error => {
        this.notification.showError(error);
        throw error;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  creditAll(ids: string[]): Observable<any> {
    this.progressBar.start();
    return this.client.post(`${this.endpoint}/credit-all`, JSON.stringify(ids), { headers: { 'Content-Type': 'application/json' } }).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  creditAllFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<any> {
    if (includedIds.length > 0) {
      return this.creditAll(includedIds);
    }
    this.progressBar.start();
    return this.client
      .post(
        `${this.endpoint}/credit-filter`,
        { filter: this.prepareFilter(filter), includedIds: includedIds, excludedIds: excludedIds, affectedItemsCount: affectedItemsCount },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  deleteFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<any> {
    if (includedIds.length > 0) {
      return this.deleteMany(includedIds);
    }
    this.progressBar.start();
    return this.client
      .post(
        `${this.endpoint}/delete-filter`,
        { filter: this.prepareFilter(filter), includedIds: includedIds, excludedIds: excludedIds, affectedItemsCount: affectedItemsCount },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  deleteMany(ids: string[]): Observable<any> {
    this.progressBar.start();
    return this.client.post(`${this.endpoint}/delete`, JSON.stringify(ids), { headers: { 'Content-Type': 'application/json' } }).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  exportFilter(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFiltered(filter, includedIds, excludedIds, affectedItemsCount, 'Fakturor.xlsx', true);
  }

  exportWithInvoiceRows(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFilteredWithUrl(
      'invoiceRows-export',
      'invoiceRows-export-filter',
      filter,
      includedIds,
      excludedIds,
      affectedItemsCount,
      'Fakturor.xlsx'
    );
  }

  export(ids: string[]): Observable<File> {
    return this.exportFilter({}, ids, [], ids.length);
  }

  getFilterDefinition(): Observable<{
    company: string[];
    companies: Company[];
    periodFrom: string;
    periodTo: string;
    invoicePeriods: InvoicePeriodItem[];
    selectedFields: string[];
    category: number;
    invDeliveryMethod: number;
    emlDeliveryStatusKind: number;
    invoiceNumber: number;
    invoiceDate: LocalDate;
    dueDate: LocalDate;
    ocr: string;
    recipientName: string;
    property: string;
    properties: Property[];
    directDebit: number;
    vatInvoice: number;
    invPeriodMonths: number;
    invoiceDeliveryMethods: InvoiceDeliveryMethod[];
    labeling: string;
    rentalId: string;
    invDeliveryMethodExecuted: number;
    recipientCategory: number;
    invoiceType: number;
    defaultFields: string[];
    companyCustomPropertiesTextValues: { key: string; value: string }[];
    companyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    companyCustomPropertiesNumericValues: { key: string; value: string }[];
    companyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          company: res.company ? res.company : [],
          companies: Array.isArray(res.companies) ? res.companies.map(x => Object.assign(new Company(), x)) : [],
          periodFrom: res.periodFrom ? res.periodFrom : '',
          periodTo: res.periodFrom ? res.periodTo : '',
          invoicePeriods: Array.isArray(res.periods) ? res.periods.map(x => Object.assign(new InvoicePeriodItem(), x)) : [],
          category: Number.isInteger(res.category) ? res.category : InvoiceCategory.Any,
          invDeliveryMethod: res.invDeliveryMethod ? res.invDeliveryMethod : 0,
          emlDeliveryStatusKind: res.emlDeliveryStatusKind ? res.emlDeliveryStatusKind : 0,
          invoiceNumber: res.invoiceNumber ? res.invoiceNumber : '',
          invoiceDate: res.invoiceDate ? res.invoiceDate : null,
          dueDate: res.dueDate ? res.dueDate : null,
          ocr: res.ocr ? res.ocr : '',
          recipientName: res.recipientName ? res.recipientName : '',
          property: res.property ? res.property : '',
          properties: Array.isArray(res.properties) ? res.properties.map(x => Object.assign(new Property(), x)) : [],
          directDebit: Number.isInteger(res.directDebit) ? res.directDebit : 0,
          vatInvoice: Number.isInteger(res.vatInvoice) ? res.vatInvoice : 0,
          invPeriodMonths: res.invPeriodMonths ? res.invPeriodMonths : 0,
          invoiceDeliveryMethods: res.invoiceDeliveryMethods,
          labeling: res.labeling ? res.labeling : '',
          rentalId: res.rentalId ? res.rentalId : '',
          invDeliveryMethodExecuted: res.invDeliveryMethodExecuted ? res.invDeliveryMethodExecuted : 0,
          recipientCategory: res.recipientCategory ? res.recipientCategory : null,
          invoiceType: res.invoiceType ? res.invoiceType : null,
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
          defaultFields: this._defaultFilterFields,
          companyCustomPropertiesTextValues: Array.isArray(res.companyCustomPropertiesTextValues)
            ? res.companyCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesNumericValues: Array.isArray(res.companyCustomPropertiesNumericValues)
            ? res.companyCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesDateValues: Array.isArray(res.companyCustomPropertiesDateValues)
            ? res.companyCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesBooleanValues: Array.isArray(res.companyCustomPropertiesBooleanValues)
            ? res.companyCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  getCreditInvoiceData(invoiceId: string): Observable<any> {
    return this.client
      .get<any>(`${this.endpoint}/credit-invoice-data/${invoiceId}`, { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          return [];
        })
      );
  }

  prepareFilter(filter: any): any {
    let filterValue = JSON.parse(JSON.stringify(filter));
    filterValue.company = Array.isArray(filterValue?.company) ? filterValue.company.filter(c => c !== '') : [];
    if (filterValue?.invoiceNumber) {
      if (isNaN(filterValue?.invoiceNumber) || Math.round(filter?.invoiceNumber * 1) !== filter?.invoiceNumber * 1) {
        filterValue.invoiceNumber = 0;
      }
      filterValue.invoiceNumber = filterValue?.invoiceNumber * 1;
    }
    return filterValue;
  }

  getLeaseAgreementInvoices(leaseAgreement: string): Observable<Invoice[]> {
    this.progressBar.start();
    return this.client.get<any[]>(`${this.endpoint}/lease-agreement`, { params: { id: leaseAgreement } }).pipe(
      map(items => items.map(item => this.createModel(item))),
      finalize(() => this.progressBar.stop())
    );
  }

  getRentalObjectInvoices(rentalObjectId: string): Observable<Invoice[]> {
    this.progressBar.start();
    return this.client.get<any[]>(`${this.endpoint}/rental-object`, { params: { id: rentalObjectId } }).pipe(
      map(items => items.map(item => this.createModel(item))),
      finalize(() => this.progressBar.stop())
    );
  }

  getReminderPdf(id: any): Observable<any> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/reminder-pdf/${id}`, { responseType: 'blob' }).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop()),
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  getLatestReminderPdfForInvoice(invoiceId: string): Observable<any> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/reminder-pdf-invoice/${invoiceId}`, { responseType: 'blob' }).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop()),
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  getPdf(id: any): Observable<any> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/pdf/${id}`, { responseType: 'blob' }).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop()),
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  getPdfWithRegulations(id: string): Observable<any> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/pdf-with-regulations/${id}`, { responseType: 'blob' }).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop()),
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  getDocument(id: any): Observable<any> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/document/${id}`, { responseType: 'blob' }).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop()),
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  getVerifications(invoiceId: string): Observable<InvoiceAccounting[]> {
    this.progressBar.start();
    return this.client.get<InvoiceAccounting[]>(`${this.endpoint}/verifications/${invoiceId}`).pipe(
      map(result => result.map(item => Object.assign(new InvoiceAccounting(), item))),
      finalize(() => this.progressBar.stop())
    );
  }

  getVerificationsWithBalance(invoiceId: string): Observable<{ verifications: InvoiceAccounting[]; balances: InvoiceAccountingBalance[] }> {
    this.progressBar.start();
    return this.client
      .get<{
        verifications: InvoiceAccounting[];
        balances: InvoiceAccountingBalance[];
      }>(`${this.endpoint}/verifications-with-balance/${invoiceId}`)
      .pipe(finalize(() => this.progressBar.stop()));
  }

  getPdfMany(invoiceIds: string[]): Observable<File> {
    this.progressBar.start();
    return this.client.post(`${this.endpoint}/pdf`, JSON.stringify(invoiceIds), { observe: 'response', responseType: 'arraybuffer' }).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop()),
      map(res => {
        return this.bufferToFile(res, 'Fakturor.zip', 'application/zip');
      })
    );
  }

  OrderPdfPrintForFilter(filter: any, includedIds: string[], excludedIds: string[], affectedItemsControlValue: number): Observable<any> {
    if (includedIds.length > 0) {
      return this.OrderPdfPrint(includedIds);
    }
    filter = this.prepareFilter(filter);
    return this.client
      .post<
        Invoice[]
      >(`${this.endpoint}/orderfilterpdf`, { filter: filter, includedIds: includedIds, excludedIds: excludedIds, affectedItemsCount: affectedItemsControlValue }, { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        tap(() => this.notification.showSuccess('SUCCESS.ORDER_PDF'))
      );
  }

  OrderPdfPrint(ids: string[]): Observable<any> {
    return this.client
      .post<Invoice[]>(`${this.endpoint}/orderpdf`, JSON.stringify(ids), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        tap(() => this.notification.showSuccess('SUCCESS.ORDER_PDF'))
      );
  }

  getTenantInvoices(tenantId: string): Observable<Invoice[]> {
    this.progressBar.start();
    return this.client.get<any[]>(`${this.endpoint}/tenant/${tenantId}`).pipe(
      map(items => items.map(item => this.createModel(item))),
      finalize(() => this.progressBar.stop())
    );
  }

  getUnpaidSelection(filter: string, onlyCredit: boolean): Observable<AutoCompleteSelectionItem<UnpaidInvoiceSelection>[]> {
    return this.client
      .post<UnpaidInvoiceSelection[]>(`${this.endpoint}/unpaidSelection`, { filter: filter, maxItems: 20, options: onlyCredit })
      .pipe(map(data => data.map(value => this.selectionModelFromUnpaidInvoice(value))));
  }

  getUnpaidSelectionForContact(contactId: string, onlyCredit: boolean): Observable<AutoCompleteSelectionItem<UnpaidInvoiceSelection>[]> {
    return this.client
      .get<UnpaidInvoiceSelection[]>(`${this.endpoint}/unpaidSelectionForContact/${contactId}/${onlyCredit}`)
      .pipe(map(data => data.map(value => this.selectionModelFromUnpaidInvoice(value))));
  }

  getSelectionItem(id: string): Observable<AutoCompleteSelectionItem<Invoice>> {
    return this.client.get<any>(`${this.endpoint}/selectionItem/${id}`).pipe(map(value => this.selectionModelFromInvoice(value)));
  }

  private selectionModelFromUnpaidInvoice(source: UnpaidInvoiceSelection): AutoCompleteSelectionItem<UnpaidInvoiceSelection> {
    if (!source) {
      return null;
    }

    const selection = {
      ...source,
      remainingAmount: new Money(source.remainingAmount.currencyCode, source.remainingAmount.amount),
    };

    return new AutoCompleteSelectionItem(selection.displayName, selection.id, selection);
  }

  private selectionModelFromInvoice(source: any): AutoCompleteSelectionItem {
    if (!source) {
      return null;
    }
    let invoice = this.createModel(source);
    invoice.remainingAmount = new Money(invoice.remainingAmount.currencyCode, invoice.remainingAmount.amount);
    return new AutoCompleteSelectionItem(invoice.toString(), invoice.id, invoice);
  }

  setDefermentDate(id: string, date: LocalDate): Observable<Invoice | any> {
    return this.client
      .post<Invoice>(`${this.endpoint}/set-deferment`, JSON.stringify({ invoiceId: id, defermentDate: date }), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        })
      );
  }

  send(form: any): Observable<Invoice | any> {
    return this.client
      .post<Invoice>(`${this.endpoint}/send`, JSON.stringify(form), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        tap(() => this.notification.showSuccess('SUCCESS.INVOICE_SENT'))
      );
  }

  sendByFilterSummary(selection: {
    filter: any;
    includedIds: string[];
    excludedIds: string[];
    affectedItemsCount: number;
  }): Observable<{ emailCount: number; mailCount: number; b2bCount: number }> {
    selection.filter = this.prepareFilter(selection.filter);
    return this.client
      .post<{ emailCount: number; mailCount: number; b2bCount: number }>(`${this.endpoint}/send-filter-sum`, selection, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        })
      );
  }

  sendByFilter(selection: { filter: any; includedIds: string[]; excludedIds: string[]; affectedItemsCount: number }): Observable<any> {
    if (selection.includedIds.length > 0) {
      return this.sendMany(selection.includedIds);
    }
    selection.filter = this.prepareFilter(selection.filter);
    return this.client.post<any>(`${this.endpoint}/send-filter`, selection, { headers: { 'Content-Type': 'application/json' } }).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      }),
      tap(() => this.notification.showSuccess('SUCCESS.INVOICES_SENT'))
    );
  }

  sendMany(ids: string[]): Observable<Invoice[] | any> {
    return this.client
      .post<Invoice[]>(`${this.endpoint}/send-many`, JSON.stringify(ids), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        tap(() => this.notification.showSuccess('SUCCESS.INVOICES_SENT'))
      );
  }

  createReminder(form: any): Observable<Invoice | any> {
    return this.client
      .post<Invoice>(`${this.endpoint}/create-reminder`, JSON.stringify(form), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        tap(() => this.notification.showSuccess('SUCCESS.REMINDER_SENT'))
      );
  }

  createReminderForFilter(
    filter: any,
    includedIds: string[],
    excludedIds: string[],
    totalAffectedItemsControlValue: number
  ): Observable<any> {
    if (includedIds.length > 0) {
      return this.createReminderMany(includedIds);
    }
    this.progressBar.start();
    return this.client
      .post<any>(
        `${this.endpoint}/create-reminder-filter`,
        {
          filter: this.prepareFilter(filter),
          includedIds: includedIds,
          excludedIds: excludedIds,
          affectedItemsCount: totalAffectedItemsControlValue,
        },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        tap(() => this.notification.showSuccess('SUCCESS.REMINDERS_SENT')),
        catchError(error => {
          this.notification.showError(error);
          return null;
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  createReminderMany(invoiceIds: string[]): Observable<any[]> {
    this.progressBar.start();
    return this.client
      .put<any[]>(`${this.endpoint}/create-reminder-many`, JSON.stringify(invoiceIds), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        tap(() => this.notification.showSuccess('SUCCESS.REMINDERS_SENT')),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  getMatchingLeaseAgreements(companyId: string, recipientId: string): Observable<InvoiceRecipientRecentLeaseAgreement[]> {
    this.progressBar.start();
    return this.client.get<any[]>(`${this.endpoint}/matching-lease-agreements/${companyId}/${recipientId}`).pipe(
      map(res => res.map(r => Object.assign(new InvoiceRecipientRecentLeaseAgreement(), r))),
      finalize(() => this.progressBar.stop())
    );
  }
}
